/**
 * The multi-agent IDE's editor save: persist the user-reviewed code as a branch (so it shows up in
 * the deployment tab) and commit the editor state.
 *
 * There is NO compile gate here. The old run lane's `validate-save` / `fix-code` routes are gone with
 * that lane; validation of a change set is the session's own check-only deploy (Lane V2), not an
 * editor-save step. A save is never blocked on validation.
 */
import type { Dispatch, SetStateAction } from "react";
import toast from "react-hot-toast";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { isHexDigit } from "@/services/multi-agent";
import type { MetadataBranch } from "@/services/metadata_v1/types";

export type SaveErrorState = {
  message: string;
  code: string;
  /** Per-attempt error log for the end-of-loop review card (#12). */
  attempts?: { attempt: number; error: string }[];
  /** Banner tone. Defaults to error (red) for backward compatibility. "warning"
   *  (amber) = the save SUCCEEDED but the code could not be compile-validated
   *  (e.g. a new class + validator unavailable). */
  severity?: "error" | "warning";
} | null;

/**
 * Canonical UUID: 8-4-4-4-12 hex digits, hyphens at positions 8/13/18/23,
 * total length 36, case-insensitive.
 */
function isUuid(s: string): boolean {
  if (s.length !== 36) return false;
  for (let i = 0; i < 36; i++) {
    const ch = s[i];
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      if (ch !== "-") return false;
    } else if (!isHexDigit(ch)) {
      return false;
    }
  }
  return true;
}

export async function runSessionSaveWithValidation(params: {
  activeFileId: string;
  className: string;
  metadataType: string;
  content: string;
  /** Existing branch for this file, if any. When undefined, `saveBranchAsync`
      creates a new branch; when defined, it updates the existing one. */
  activeBranch?: MetadataBranch | null;
  /** Async form of the `useSaveBranchFile` mutation — persists code as a branch
      so it shows up in the deployment tab. */
  saveBranchAsync: (input: {
    metadataItemId: string;
    content: string;
    name?: string;
  }) => Promise<MetadataBranch>;
  /** When provided (file is virtual — not yet registered in ncoder-backend),
      used to create the metadata_item row before the branch save. */
  ensureMetadataItemId?: () => Promise<string | null>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSaveErrors: Dispatch<SetStateAction<SaveErrorState>>;
  setContentByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  setDirtyByFileId: Dispatch<SetStateAction<Record<string, boolean>>>;
  setOriginalByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  onFileSaved?: (data: {
    id: string;
    fileName: string;
    metadataType: string;
  }) => void;
  /** When branch save used a canonical metadata UUID different from the tab id (virtual → real). */
  onTabIdResolved?: (canonicalId: string, previousTabId: string) => void;
}): Promise<void> {
  const {
    activeFileId,
    className,
    metadataType,
    content,
    activeBranch,
    saveBranchAsync,
    ensureMetadataItemId,
    setIsSaving,
    setSaveErrors,
    setContentByFileId,
    setDirtyByFileId,
    setOriginalByFileId,
    onFileSaved,
    onTabIdResolved,
  } = params;

  // getState() — this module is a plain async function, not a component, so the
  // `useMultiAgentIdeStore(selector)` hook form would throw React #321.
  const ide = useMultiAgentIdeStore.getState();

  setIsSaving(true);
  setSaveErrors(null);

  const currentCode = content;

  /** Persist `currentCode` as a branch and commit editor state. */
  const persistBranchAndCommit = async (): Promise<void> => {
    let persistKey = activeFileId;
    const saveTo = (itemId: string) =>
      saveBranchAsync({
        metadataItemId: itemId,
        content: currentCode,
        name: activeBranch ? undefined : `edit-${className}`,
      });
    try {
      let itemId = isUuid(activeFileId)
        ? activeFileId
        : await ensureMetadataItemId?.();
      if (!itemId) {
        throw new Error(
          "This file isn't registered yet and no create path is available.",
        );
      }
      try {
        await saveTo(itemId);
      } catch (firstErr) {
        // The tab id may be an ARTIFACT id (refactor opened via Compare), not a
        // real metadata_item — saving against it 404s with "Metadata item not
        // found". Resolve the item by CLASS NAME and retry once.
        const resolved = await ensureMetadataItemId?.();
        if (resolved && resolved !== itemId) {
          await saveTo(resolved);
          itemId = resolved;
        } else {
          throw firstErr;
        }
      }
      persistKey = itemId;
    } catch (branchErr: unknown) {
      const branchMsg =
        branchErr && typeof branchErr === "object" && "message" in branchErr
          ? String((branchErr as Error).message)
          : "Failed to save branch";
      setSaveErrors({ message: `Branch save failed: ${branchMsg}`, code: currentCode });
      toast.error("Branch save failed");
      return;
    }

    const prevKey = activeFileId;
    setContentByFileId((prev) => {
      const next = { ...prev };
      if (persistKey !== prevKey) delete next[prevKey];
      next[persistKey] = currentCode;
      return next;
    });
    setDirtyByFileId((prev) => {
      const next = { ...prev };
      if (persistKey !== prevKey) delete next[prevKey];
      next[persistKey] = false;
      return next;
    });
    setOriginalByFileId((prev) => {
      const next = { ...prev };
      if (persistKey !== prevKey) delete next[prevKey];
      next[persistKey] = currentCode;
      return next;
    });
    if (persistKey !== prevKey) {
      onTabIdResolved?.(persistKey, prevKey);
      const prevBadge = ide.artifactBadges[prevKey];
      if (prevBadge) ide.setArtifactBadge(persistKey, prevBadge);
    }
    ide.setArtifactBadge(persistKey, "M");
    setSaveErrors(null);
    onFileSaved?.({ id: persistKey, fileName: className, metadataType });
  };
  try {
    await persistBranchAndCommit();
    toast.success("Saved");
  } catch (err: unknown) {
    const msg =
      err && typeof err === "object" && "message" in err
        ? String((err as Error).message)
        : "Save failed";
    toast.error(msg);
    setSaveErrors({ message: msg || "Save failed", code: currentCode });
  } finally {
    setIsSaving(false);
  }
}
